<script setup>
  import { computed, ref, watch } from 'vue';
  // 全局注册form-item会不兼容IE
  import { FormItem } from 'element-ui';
  import { Notify, Toast } from 'vant';
  import CountryCode from '@/component/CountryCode.vue';
  import GetCode from '@/component/GetCode.vue';
  import ImgCode from '@/component/ImgCode.vue';
  import MultiAccounts from '@/component/MultiAccounts.vue';
  import CrossBorder from '@/component/CrossBorder.vue';
  import { BUTTON_TYPE, CAPTCHA_TYPE, CONTEXT, ERROR_CODE, TRIGGER_TYPE } from '@/const';
  import i18n from '@/i18n';
  import util from '@/util';
  import service from '@/service';
  import { useCountDown } from '@/hook';
  import Agreement from '@/component/userAgreements/AgreementService';

  // 声明组件别名
  const ElFormItem = FormItem;
  const pageExpired = ref(false);

  const formRef = ref();
  const formData = ref({
    countryCode: '',
    phoneNumber: '',
    verificationCode: '',
    pageToken: '',
  });
  const triggerType = ref();
  const buttonType = ref(BUTTON_TYPE.default);

  const useCountryCode = () => {
    const countryCodeList = ref([]);
    const twoCode = computed(
      () => countryCodeList.value.find(e => e?.value === formData.value.countryCode)?.key
    );

    const getCountryCode = async () => {
      const res = await service.getCountryCodeList();
      const codeList = res[i18n.locale];
      if (!codeList) {
        return;
      }
      countryCodeList.value = codeList.map(e => ({
        key: e.twoCode,
        value: `+${e.areaCode} ${e.name}`,
        label: `+${e.areaCode} ${e.name}`,
      }));
      if (i18n.locale === 'zh_CN') {
        formData.value.countryCode = '+86 中国大陆';
      }
    };

    getCountryCode();

    return {
      countryCodeList,
      twoCode,
    };
  };
  const { countryCodeList, twoCode } = useCountryCode();

  const useValidate = () => {
    const errorMessage = ref('');

    const validator = (rule, value, callback) => {
      const { countryCode, phoneNumber, verificationCode } = formData.value;
      if (!countryCode) {
        callback(i18n.t('errorCode.IDC-01-00024'));
      }
      if (!phoneNumber) {
        callback(i18n.t('errorCode.phoneNumberEmpty'));
      }
      if (triggerType.value === TRIGGER_TYPE.submit && !verificationCode) {
        callback(i18n.t('errorCode.IDC-03-00033'));
      }
      if (errorMessage.value) {
        callback(errorMessage.value);
      }
      callback();
    };
    const rules = ref({
      verificationCode: [
        {
          validator,
          trigger: 'none',
        },
      ],
    });

    watch(errorMessage, () => formRef.value.validate(() => {}));
    watch(
      [
        () => formData.value.countryCode,
        () => formData.value.phoneNumber,
        () => formData.value.verificationCode,
      ],
      () => {
        countDown.value = 0;
        errorMessage.value = '';
      }
    );

    return {
      errorMessage,
      rules,
    };
  };
  const { errorMessage, rules } = useValidate();

  const { countDown, timeRemaining, countDownText } = useCountDown();
  // 操作风险倒计时
  const handleOperationRisk = errorMsg => {
    const lockedTime = errorMsg.split(' ').find(x => /[0-9]/.test(x));
    if (lockedTime) {
      countDown.value = Number(lockedTime);
      watch(countDownText('errorCode.operationRisk'), val => (errorMessage.value = val));
    }
  };
  const useImgCode = () => {
    const showImgCode = ref(false);
    const inputImgCode = ref();
    const timeStamp = ref();
    const imgSrc = ref();

    const getImgRandomCode = async () => {
      const res = await service.getUidCode();
      timeStamp.value = res.headers['idaas-code'] || '';
      const reader = new FileReader();
      reader.onload = e => {
        imgSrc.value = e.target.result;
      };
      reader.readAsDataURL(res.data);
    };

    const confirmImgCode = async (action, done) => {
      if (action === 'cancel') {
        buttonType.value = BUTTON_TYPE.default;
        done();
        return;
      }
      if (!inputImgCode.value) {
        Toast.fail(i18n.t('errorCode.IDC-00-00013').toString());
        done(false);
        return;
      }
      const params = {
        smsFigureCode: inputImgCode.value,
        timeStamp: timeStamp.value,
      };
      await getSMSCode(params, errorCode => {
        // 图形验证码错误
        if (ERROR_CODE.imgCode.includes(errorCode)) {
          Toast.fail(i18n.t(`errorCode.${errorCode}`).toString());
          getImgRandomCode();
          done(false);
        } else {
          errorMessage.value = i18n.t(`errorCode.${errorCode}`);
        }
      });
      done();
    };

    watch(showImgCode, val => {
      if (val) {
        inputImgCode.value = '';
        getImgRandomCode();
      }
    });

    return {
      imgSrc,
      showImgCode,
      inputImgCode,
      confirmImgCode,
      getImgRandomCode,
    };
  };
  const { imgSrc, showImgCode, inputImgCode, confirmImgCode, getImgRandomCode } = useImgCode();

  const useCaptcha = () => {
    const captchaType = ref();
    const ticket = ref('');

    let captchaOptions = {};
    let commonOptions = {};
    let captchaJsLoaded = false;
    let captchaJsReqTimes = 0;
    let captchaJsUrlList = [];
    let hasTriedChina = false;
    let captchaJsReqTimeout = 0;

    const resetButton = () => {
      if (triggerType.value === TRIGGER_TYPE.getCode) {
        buttonType.value = BUTTON_TYPE.default;
      }
    };

    const handleCaptchaErr = () => {
      if (captchaType.value === CAPTCHA_TYPE.captcha) {
        onCaptchaError();
      } else {
        // 提示错误信息，恢复按钮
        Notify(i18n.t('verifyFailedAndPlsRetry'));
        resetButton();
      }
    };

    const getLanguage = () => {
      if (['zh_CN', 'en_US'].includes(i18n.locale)) {
        return i18n.locale.replace('_', '-');
      } else {
        return i18n.locale.split('_')[0];
      }
    };
    const addScript = async () => {
      const length = captchaJsUrlList.length;
      if (length === captchaJsReqTimes || length === 0) {
        if (hasTriedChina) {
          console.log('retry end');
          return;
        }
        if (captchaOptions.currentArea !== 'china') {
          captchaJsReqTimes = 0;
          captchaOptions.currentArea = 'china';
          captchaOptions.serviceDomain = captchaOptions.areaResource.china.serviceDomain;
          hasTriedChina = true;
          if (captchaOptions.areaResource.china.captchaJs.length > 0) {
            captchaJsUrlList = [captchaOptions.areaResource.china.captchaJs[0]];
            addScript();
          } else {
            return;
          }
        }
      }

      const url = captchaJsUrlList[captchaJsReqTimes++];
      if (!url) {
        return;
      }
      const xhr = new XMLHttpRequest();
      if (captchaJsReqTimeout !== 0) {
        xhr.timeout = captchaJsReqTimeout * 1000;
      }
      xhr.open('GET', url, true);
      xhr.onload = function () {
        if ((xhr.status >= 200 && xhr.status < 300) || xhr.status === 304) {
          // 请求成功
          eval(xhr.responseText);
          captchaJsLoaded = true;
          console.log('Script loaded successfully');
        }
      };
      xhr.ontimeout = function () {
        console.log('captcha js request timeout');
        addScript();
      };
      xhr.onerror = function () {
        addScript();
        console.log('Script request failed');
      };
      xhr.send();
    };

    const getCaptchaOptions = firstVerifyRes => {
      const { appId, businessId, sceneId, serviceDomain } = captchaOptions;
      const { challenge, hcg, hct, captchaType } = firstVerifyRes;
      commonOptions = {
        appId,
        businessId,
        sceneId,
        type: captchaType,
        lang: getLanguage(),
      };
      return {
        ...commonOptions,
        challenge,
        hcg,
        hct,
        serviceDomain: [serviceDomain],
        mode: 'popup',
        width: '262px',
        element: '#captcha',
        isCloseable: true,
        popupStyles: {
          position: 'fixed',
          top: '240px',
        },
      };
    };

    const onCaptchaError = () => {
      resetButton();
      showImgCode.value = true;
    };

    const onCaptchaSuccess = async successData => {
      if (successData.resultCode !== 0) {
        return;
      }
      try {
        commonOptions.callCountryCode = formData.value.countryCode.split(' ')[0];
        const res = await service.secondVerify(commonOptions, successData, formData.value);
        if (res.resultCode !== 0) {
          Notify(i18n.t('verifyFailedAndPlsRetry'));
          resetButton();
          return;
        }
        ticket.value = res.random;
        if (triggerType.value === TRIGGER_TYPE.getCode) {
          await getSMSCode({
            ticket: ticket.value,
          });
        }
      } catch ({ response }) {
        const { status, data } = response;
        const { errorCode } = data;
        if (!status) {
          Notify(i18n.t('serviceErrAndPlsRetry'));
        }
        if (status === 400) {
          const codeSwitchNumber = captchaType.value;
          if (
            codeSwitchNumber == 2 &&
            (errorCode === 'IDC-03-0062' || errorCode === 'IDC-10-00500')
          ) {
            onCaptchaError();
          } else {
            // 提示服务出错，文案待定
            Notify(i18n.t('serviceErrAndPlsRetry'));
            resetButton();
          }
        } else if (parseInt(status / 100) == 5) {
          onCaptchaError();
        }
      }
    };

    const initCaptcha = initOption => {
      let captchaInstance = null;
      const init = () => captchaInstance.popupShow();
      const success = successData => onCaptchaSuccess(successData);
      const close = () => resetButton();
      const error = err => {
        handleCaptchaErr();
        if (err && err.errorCode == 400 && err.errorMsg) {
          if (err.errorMsg.indexOf(503) > -1) {
            captchaInstance.destroy();
          }
        }
      };
      // eslint-disable-next-line no-undef
      captchaInstance = initHuaweiCaptcha(initOption, init, success, close, error);
    };

    const showCaptcha = async () => {
      if (!captchaJsLoaded) {
        if (captchaType.value === 2) {
          onCaptchaError();
        } else {
          Notify(i18n.t('resourceReqFailed'));
          resetButton();
        }
        return;
      }
      captchaOptions.callCountryCode = formData.value.countryCode.split(' ')[0];
      try {
        const res = await service.firstVerify(captchaOptions);
        if (res?.resultCode === 0) {
          initCaptcha(getCaptchaOptions(res));
        } else {
          Notify(i18n.t('serviceErrAndPlsRetry'));
          resetButton();
        }
      } catch ({ response }) {
        const { status, data } = response;
        const { errorCode } = data;
        if (!status) {
          Notify(i18n.t('serviceErrAndPlsRetry'));
        }
        if (status === 400) {
          const codeSwitchNumber = captchaType.value;
          if (
            codeSwitchNumber == 2 &&
            (errorCode === 'IDC-03-0062' || errorCode === 'IDC-10-00500')
          ) {
            onCaptchaError();
          } else {
            Notify(i18n.t('serviceErrAndPlsRetry'));
            resetButton();
          }
        } else if (parseInt(status / 100) == 5) {
          onCaptchaError();
        }
      }
    };

    service.getCodeSwitch().then(async res => {
      const codeSwitch = res.data;
      captchaType.value = codeSwitch;
      formData.value.pageToken = res.headers['sm-page-token'] || '';
      if (codeSwitch === CAPTCHA_TYPE.captcha || codeSwitch === CAPTCHA_TYPE.pureCaptcha) {
        const res = await service.getCaptchaParams();
        captchaOptions = res;
        captchaJsReqTimeout = res.timeLimit;
        const currentArea = captchaOptions.currentArea || '';
        if (currentArea) {
          if (currentArea === 'china') {
            hasTriedChina = true;
          }
          captchaJsUrlList = captchaOptions.areaResource[currentArea].captchaJs;
          addScript();
        }
      }
    });

    return {
      captchaType,
      ticket,
      showCaptcha,
    };
  };
  const { captchaType, ticket, showCaptcha } = useCaptcha();

  watch(
    [() => formData.value.countryCode, () => formData.value.phoneNumber],
    () => (Agreement.hasAgree = false)
  );

  const useSMSCode = () => {
    const safeKey = ref('');

    const getSMSCode = async (params = {}, onError) => {
      buttonType.value = BUTTON_TYPE.loading;
      try {
        await service.sendSMSCode(formData.value, params);
        buttonType.value = BUTTON_TYPE.start;
        errorMessage.value = '';
        Toast.success(i18n.t('toast.hasSend').toString());
      } catch ({ response }) {
        const { status, data } = response;
        const { errorCode, errorMsg } = data;
        if (status !== 400 || !errorCode) {
          buttonType.value = BUTTON_TYPE.default;
          return;
        }
        if (ERROR_CODE.riskControl.includes(errorCode)) {
          const { errorData } = data;
          const key = errorData?.[0]?.safekey;
          if (key) {
            safeKey.value = key;
          }
          if (captchaType.value === CAPTCHA_TYPE.imgCode) {
            showImgCode.value = true;
          } else {
            await showCaptcha(TRIGGER_TYPE.getCode);
          }
        }
        // 操作风险倒计时
        else if (ERROR_CODE.operationRisk.includes(errorCode)) {
          handleOperationRisk(errorMsg);
          buttonType.value = BUTTON_TYPE.default;
        } else if (errorCode === 'IDC-00-00017') {
          pageExpired.value = true;
        }
        // 图形验证码弹窗回调
        else if (onError) {
          onError(errorCode);
        } else {
          errorMessage.value = i18n.t(`errorCode.${errorCode}`);
          buttonType.value = BUTTON_TYPE.default;
        }
      }
    };

    const clickGetCode = () => {
      triggerType.value = TRIGGER_TYPE.getCode;
      const { countryCode, phoneNumber } = formData.value;
      if (!countryCode || !phoneNumber) {
        formRef.value.validate(() => {});
        return;
      }
      if (timeRemaining.value) {
        return;
      }
      const length = phoneNumber.length;
      if (length < 6 || length > 30 || !/^\d+$/.test(phoneNumber)) {
        errorMessage.value = i18n.t('errorCode.IDC-02-00028');
        return;
      }
      errorMessage.value = '';
      Agreement.open({
        countryCode: formData.value.countryCode,
        confirm: getSMSCode,
      });
    };

    return {
      safeKey,
      getSMSCode,
      clickGetCode,
    };
  };
  const { safeKey, getSMSCode, clickGetCode } = useSMSCode();

  const useMultiAccount = () => {
    const showMultiAccount = ref(false);
    const accounts = ref([]);
    const accountUid = ref('');
    const accountType = computed(
      () => accounts.value.find(e => e?.uid === accountUid.value)?.accountType || ''
    );

    return {
      showMultiAccount,
      accounts,
      accountUid,
      accountType,
    };
  };
  const { showMultiAccount, accounts, accountUid, accountType } = useMultiAccount();

  const useCrossBorder = () => {
    const showCrossBorder = ref(false);
    const crossBorderInfo = ref({});

    const confirmCrossBorder = async () => {
      const toast = Toast.loading({
        duration: 0,
        forbidClick: true,
        message: i18n.t('toast.wait').toString(),
      });
      try {
        const res = await service.crossBorderAuth(crossBorderInfo.value);
        const { statusText } = res;
        if (statusText !== 'SUCCESS') {
          return;
        }
        const { pwdPreOverdue, pwdRemainDays } = crossBorderInfo.value;
        const { accountInfo } = res;
        if (!pwdPreOverdue) {
          jumpToTarget(accountInfo);
          return;
        }
        const { pwdRedirectKey } = accountInfo;
        const redirect = util.getURLSearchParam('redirect');
        const redirectParams = {
          uid: accountUid.value,
          pwdRedirectKey,
        };
        if (accountType.value) {
          redirectParams.accountType = accountType.value;
        }
        util.jumpTo(`${CONTEXT.uniportal}/login-PsdHasExpired.html`, {
          overdue: pwdPreOverdue,
          days: pwdRemainDays,
          time: accountInfo.pwdRemainDays,
          redirect,
          ...redirectParams,
        });
      } catch {
        // eslint-disable-next-line no-empty
      } finally {
        toast.clear();
      }
    };

    return {
      showCrossBorder,
      crossBorderInfo,
      confirmCrossBorder,
    };
  };
  const { showCrossBorder, crossBorderInfo, confirmCrossBorder } = useCrossBorder();

  const useAppUrl = () => {
    const appUrl = ref('');

    const getAppUrl = async () => {
      const res = await service.getDefaultRedirect();
      appUrl.value = util.getURLSearchParam('redirect') || util.getURLSearchParam('appUrl') || res;
    };
    getAppUrl();

    return {
      appUrl,
    };
  };
  const { appUrl } = useAppUrl();

  const useSubmit = () => {
    const isMtokenApp = (appType, typeKey) => {
      // 支持的类型名未传递，提示不支持
      if (!typeKey) {
        return false;
      }
      // appType参数为空，则表示支持全部类型
      if (!appType) {
        return true;
      }
      // 指定的集成类型需要包含在登记的类型列表中
      return appType.includes(typeKey);
    };

    // 跳转其他应用的公共函数
    const handleLoad = ({ result, error, data }, loader) => {
      if (result === 'success' && data) {
        loader(data);
      } else {
        loader(error);
      }
    };

    const handleSDK = res => {
      if (!isMtokenApp(res.appType, 'MOBILE')) {
        return;
      }
      if (window.webLoader?.intoApp) {
        handleLoad(res, data => window.webLoader.intoApp(data));
      }
    };

    const handleCS = res => {
      if (!isMtokenApp(res.appType, 'CS')) {
        return;
      }
      if (window.external?.intoProgram) {
        handleLoad(res, data => window.external.intoProgram(data));
      }
      if (window.intoProgram) {
        handleLoad(res, data => window.intoProgram(data));
      }
    };

    const getMobileResource = async (mtokenKey, callback = () => {}) => {
      const isWindowsProgram = /windowsprogram/i.test(navigator.userAgent);
      const appId = util.getAppId();
      const isPhoneService = /phoneservice/i.test(navigator.userAgent); // 移动sdk
      const isMiniProgram = /miniprogram/i.test(navigator.userAgent);
      if (!appId && !isPhoneService && !isWindowsProgram && !isMiniProgram) {
        if (callback) {
          callback();
        }
        return;
      }
      const res = await service.getMobileResource(mtokenKey);
      const rememberAccountNameMobile = util.getCookie('rememberAccountNameMobile');
      if (rememberAccountNameMobile && res?.data) {
        const atobData = JSON.parse(atob(res.data));
        atobData.rememberAccountNameMobile = rememberAccountNameMobile;
        res.data = btoa(JSON.stringify(atobData));
      }
      if (appId || isPhoneService) {
        // 移动端跳转对应应用
        handleSDK(res);
      }
      if (appId || isWindowsProgram) {
        handleCS(res);
      }
      if (callback) {
        callback();
      }
    };

    const jumpToDoubleFactor = ({ mfaKey, maskUid, mtokenKey, pwdRedirectKey }) => {
      const params = {
        redirect: '',
        maskUid,
        mfaKey,
        mtokenKey,
        pwdRedirectKey,
        uid: accountUid.value,
      };
      if (accountType.value) {
        params.accountType = accountType.value;
      }
      util.jumpTo(`${CONTEXT.uniportal}login-doubleFactor.html`, params);
    };

    const jumpToAgreementProcess = async accountInfo => {
      const { needToComplete, pwdPreOverdue } = accountInfo;
      const { needSign, needSignKey, uuid, uid, uniid, maskUid, mtokenKey, userKey } = accountInfo;
      const { overdue, pwdRemainDays, category, redirect, pwdRedirectKey, bifactor } = accountInfo;
      let redirectURL = '';
      const targetURL = `${CONTEXT.uniportal}/updatePrivacy.html`;
      let targetParams = {
        maskUid,
        mtokenKey,
      };
      // 密码过期路径参数
      const pwdExpiredParams = {
        overdue,
        days: pwdRemainDays,
        category,
        redirect,
        mtokenKey,
        uid: accountUid.value,
        pwdRedirectKey,
      };
      if (accountType.value) {
        pwdExpiredParams.accountType = accountType.value;
      }
      if (needToComplete && !pwdPreOverdue) {
        // 协议签署 + 完善用户信息
        redirectURL = util.assembleURL(`${CONTEXT.uniportal}/completeUserInfo.html`, {
          maskUid,
          userKey,
        });
        targetParams = {
          redirect: redirectURL,
          ...targetParams,
          needToComplete,
        };
      } else if (!needToComplete && pwdPreOverdue) {
        // 协议签署 + 密码即将过期
        redirectURL = util.assembleURL(
          `${CONTEXT.uniportal}/login-PsdWillExpired.html`,
          pwdExpiredParams
        );
        targetParams = {
          redirect: redirectURL,
          ...targetParams,
          pwdPreOverdue,
        };
      } else if (needToComplete && pwdPreOverdue) {
        // 协议签署 + 完善用户信息 + 密码即将过期
        const tempURL = util.assembleURL(
          `${CONTEXT.uniportal}/login-PsdWillExpired.html`,
          pwdExpiredParams
        );
        redirectURL = util.assembleURL(`${CONTEXT.uniportal}/completeUserInfo.html`, {
          pwdPreOverdue,
          redirect: tempURL,
          maskUid,
          userKey,
        });
        targetParams = {
          redirect: redirectURL,
          ...targetParams,
          pwdPreOverdue,
          needToComplete,
        };
      } else {
        // 协议签署
        redirectURL = redirect;
        targetParams = {
          redirect: redirect,
          bifactor,
          ...targetParams,
        };
      }
      // util.jumpTo(targetURL, targetParams);
      // 不跳转URL，手动调用同意协议接口，下发Cookie
      await service.updateSignAgreement({
        uid,
        uniid,
        uuid,
        needSign,
        needSignKey,
      });
      if (bifactor) {
        location.href = util.assembleURL(`${CONTEXT.uniportal}/login-doubleFactor.html`, {});
        return;
      }
      if (needToComplete || pwdPreOverdue) {
        if (util.isSDK()) {
          await getMobileResource(mtokenKey);
        } else {
          location.href = util.escapeXss(redirectURL);
        }
        return;
      }
      const params = {
        redirect: redirectURL,
        maskUid,
      };
      const wap = util.getURLSearchParam('wap');
      if (wap === '1') {
        params.wap = '1';
      }
      const idaasExtField = util.getURLSearchParam('idaas_ext_field');
      if (idaasExtField) {
        params['idaas_ext_field'] = idaasExtField;
      }
      if (mtokenKey) {
        params.mtokenKey = mtokenKey;
      }
      util.jumpTo(`${CONTEXT.uniportal}/commonRedirectionPage.html`, params);
    };

    const jumpToTarget = ({ redirect, maskUid, mtokenKey }) => {
      const isWindowsProgram = /windowsprogram/i.test(navigator.userAgent);
      if (isWindowsProgram) {
        util.jumpTo(`${CONTEXT.uniportal}/commonRedirectionPage.html`, {
          redirect,
          maskUid,
          mtokenKey,
        });
      } else {
        getMobileResource(mtokenKey, () => {
          location.href = util.escapeXss(redirect);
        });
      }
    };

    const handleMultiAccounts = ({ errorData }) => {
      const accountsInfos = errorData?.[0];
      if (!Array.isArray(accountsInfos)) {
        return;
      }
      accounts.value = accountsInfos;
      if (accountsInfos.length > 0) {
        accountUid.value = accountsInfos[0].uid;
      }
      showMultiAccount.value = true;
    };

    // 账号即将锁定提示
    const handleAccountLock = errorData => {
      const failLogonCount = errorData[0]?.failLogonCount;
      if (failLogonCount) {
        const remainingTimes = 5 - Number(failLogonCount);
        errorMessage.value = i18n.tc('errorCode.accountLock', remainingTimes, {
          number: remainingTimes,
        });
      }
    };

    // 账号已锁定倒计时
    const handleAccountLocked = errorData => {
      const lockedTime = errorData[0]?.lockedTime;
      if (lockedTime) {
        countDown.value = Number(lockedTime);
        watch(countDownText('errorCode.accountLocked'), val => (errorMessage.value = val));
      }
    };

    const handleSubmit = async accountUid => {
      const toast = Toast.loading({
        duration: 0,
        forbidClick: true,
        message: i18n.t('toast.wait').toString(),
      });
      try {
        const res = await service.loginOrRegister(formData.value, {
          // safeKey: safeKey.value,
          ticket: ticket.value,
          uid: accountUid,
          agreement: Agreement.hasAgree,
          appUrl: appUrl.value,
          twoCode: twoCode.value,
          // imgCode: inputImgCode.value,
        });
        if (!res) {
          return;
        }
        errorMessage.value = '';
        const { statusText, accountInfo } = res;
        const { bifactor, needSign, crossBorder } = accountInfo;
        if (statusText === 'SUCCESS' && bifactor) {
          jumpToDoubleFactor(accountInfo);
          return;
        }
        if (needSign) {
          await jumpToAgreementProcess(accountInfo);
          return;
        }
        if (statusText !== 'SUCCESS') {
          return;
        }
        if (crossBorder) {
          const { crossBorderKey, pwdPreOverdue, pwdRemainDays } = accountInfo;
          crossBorderInfo.value = {
            crossBorderKey,
            pwdPreOverdue,
            pwdRemainDays,
          };
          showCrossBorder.value = true;
        } else {
          jumpToTarget(accountInfo);
        }
      } catch ({ response }) {
        if (!response) {
          errorMessage.value = i18n.t('errorCode.IDC-10-00400');
          return;
        }
        const { status, data } = response;
        const { errorCode, errorData, errorMsg } = data;
        if (status !== 400 || !errorCode) {
          return;
        }
        // 5次账号锁定
        if (ERROR_CODE.accountLock.includes(errorCode)) {
          handleAccountLock(errorData);
        }
        // 账号已锁定
        else if (ERROR_CODE.accountLocked.includes(errorCode)) {
          handleAccountLocked(errorData);
        }
        // 操作风险倒计时
        else if (ERROR_CODE.operationRisk.includes(errorCode)) {
          handleOperationRisk(errorMsg);
        }
        // 多账号场景
        else if (ERROR_CODE.multiAccounts.includes(errorCode)) {
          handleMultiAccounts(data);
        }
        // 系统内部错误
        else if (ERROR_CODE.internalError.includes(errorCode)) {
          errorMessage.value = i18n.t('errorCode.IDC-10-00500');
        } else if (errorCode === 'IDC-00-00017') {
          pageExpired.value = true;
        } else {
          errorMessage.value = i18n.t(`errorCode.${errorCode}`);
        }
      } finally {
        toast.clear();
      }
    };

    const clickSubmit = async () => {
      triggerType.value = TRIGGER_TYPE.submit;
      const { countryCode, phoneNumber, verificationCode } = formData.value;
      if (!countryCode || !phoneNumber || !verificationCode) {
        formRef.value.validate(() => {});
        return;
      }
      if (timeRemaining.value) {
        return;
      }
      errorMessage.value = '';
      await handleSubmit();
    };

    return {
      handleSubmit,
      clickSubmit,
      jumpToTarget,
    };
  };
  const { handleSubmit, clickSubmit, jumpToTarget } = useSubmit();
  const refreshPage = () => {
    location.reload();
  };
</script>

<template>
  <div>
    <div class="px-5">
      <el-form :model="formData" :rules="rules" ref="formRef" class="custom-form">
        <el-form-item prop="phoneNumber">
          <div class="flex">
            <country-code
              v-model="formData.countryCode"
              :country-code-list="countryCodeList"
            ></country-code>
            <el-input
              :placeholder="$t('placeholder.phoneNumber')"
              v-model="formData.phoneNumber"
            ></el-input>
          </div>
        </el-form-item>
        <el-form-item prop="verificationCode">
          <el-input
            class="custom-input"
            maxlength="6"
            :placeholder="$t('placeholder.verificationCode')"
            v-model="formData.verificationCode"
          >
            <template #suffix>
              <get-code :type="buttonType" @click="clickGetCode"></get-code>
            </template>
          </el-input>
        </el-form-item>
      </el-form>
      <el-button class="submit w-full mt-10" round type="primary" @click="clickSubmit">
        {{ $t('button.submit') }}
      </el-button>
      <div class="tip text-center mt-2 text-xs leading-5">{{ $t('layout.tip') }}</div>
    </div>
    <div id="captcha"></div>
    <img-code
      :visible.sync="showImgCode"
      v-model="inputImgCode"
      :img-src="imgSrc"
      :before-close="confirmImgCode"
      @changeImg="getImgRandomCode"
    ></img-code>
    <cross-border :visible.sync="showCrossBorder" @confirm="confirmCrossBorder"></cross-border>
    <multi-accounts
      :visible.sync="showMultiAccount"
      v-model="accountUid"
      :accounts="accounts"
      @confirm="handleSubmit(accountUid)"
    ></multi-accounts>

    <van-dialog
      v-model="pageExpired"
      :confirm-button-text="$t('dialog.knowMe')"
      @confirm="refreshPage"
      get-container="body"
      class="page-expired-dialog"
    >
      <div style="text-align: center">{{ $t('errorCode.IDC-00-00017') }}</div>
    </van-dialog>
  </div>
</template>

<style lang="less" scoped>
  .page-expired-dialog {
    :deep(.van-dialog__content) {
      padding: 24px;
    }
  }

  .submit {
    border: none;
    height: 40px;
    background: #c8000b !important;
  }

  .tip {
    color: #aeaeae;
  }

  .custom-input {
    :deep(.el-input__inner) {
      padding: 0 8px;
    }

    :deep(.el-input__suffix) {
      right: 0;
    }
  }

  .custom-form {
    :deep(.el-input__inner) {
      color: #191919;
      border-left: none;
      border-top: none;
      border-right: none;
      border-radius: 0;
      height: 48px;
    }

    :deep(.el-form-item) {
      margin-bottom: 8px;
    }

    :deep(.el-button--text) {
      color: #0067d1 !important;

      &:active {
        color: #004ea8 !important;
      }
    }

    :deep(.el-form-item.is-error) {
      .el-input__inner,
      .el-textarea__inner {
        &,
        &:focus {
          border-color: #dcdfe6;
        }
      }
    }

    :deep(.el-message-box__input) input.invalid {
      &,
      &:focus {
        border-color: #dcdfe6;
      }
    }
  }
</style>
